import React, { useState } from "react";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { Button } from "@progress/kendo-react-buttons";
import { Checkbox } from "@progress/kendo-react-inputs";
import { useInjection } from "@presentation/components/dependencyInjection";
import { TeamManagementService } from "@application/useCases/workspace/TeamManagementService";
import { extractProblemDetails } from "@presentation/common/ErrorHelpers";
import { describeManagerInvariantDenial } from "@presentation/common/utils/managerInvariantDenial";
import { formatUserDisplayName, useUserDisplayNames } from "@presentation/hooks/useUserDisplayNames";
import type { TeamDetail } from "@domain/schemas/workspace";
import { useWorkspaceSuccessNotification } from "@presentation/components/notifications/useWorkspaceSuccessNotification";

interface RemoveTeamMemberDialogProps {
  teamId: string;
  userId: string;
  onClose: () => void;
  onSuccess: (updated: TeamDetail) => void;
}

/**
The cascade choice is explicit and unchecked by default - never inferred, never pre-checked.
*/
export const RemoveTeamMemberDialog: React.FC<RemoveTeamMemberDialogProps> = ({ teamId, userId, onClose, onSuccess }) => {
  const teamManagementService = useInjection(TeamManagementService);
  const notifySuccess = useWorkspaceSuccessNotification();
  const userNames = useUserDisplayNames(React.useMemo(() => [userId], [userId]));
  const [alsoRemoveProjectRoles, setAlsoRemoveProjectRoles] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleConfirm = async () => {
    setIsSubmitting(true);
    setErrorMessage(null);
    try {
      const updated = await teamManagementService.removeTeamMemberAsync(teamId, userId, alsoRemoveProjectRoles);
      notifySuccess("team", "memberRemoved");
      onSuccess(updated);
      onClose();
    } catch (error) {
      setErrorMessage(describeManagerInvariantDenial(error, "team") ?? extractProblemDetails(error).detail ?? extractProblemDetails(error).title ?? "Could not remove this member.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog title="Remove member" onClose={onClose} width={420}>
      <div style={{ display: "grid", gap: 16 }}>
        <p style={{ margin: 0 }}>
          Remove <strong>{formatUserDisplayName(userNames[userId], userId)}</strong> from this team?
        </p>
        <label style={{ display: "flex", alignItems: "flex-start", gap: 8, cursor: "pointer" }}>
          <Checkbox checked={alsoRemoveProjectRoles} onChange={(event) => setAlsoRemoveProjectRoles(Boolean(event.value))} />
          <span>Also remove their project-level roles in this team&apos;s projects</span>
        </label>
        {errorMessage && <div style={{ color: "#7f1d1d" }}>{errorMessage}</div>}
      </div>
      <DialogActionsBar>
        <Button onClick={onClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button themeColor="primary" disabled={isSubmitting} onClick={handleConfirm}>
          Remove
        </Button>
      </DialogActionsBar>
    </Dialog>
  );
};
